import React, { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import { normalizeRichText } from '../../../components/RichContent';
import AdminModal from '../AdminModal';
import SearchSelect, { type SearchOption } from '../SearchSelect';
import { searchEndorserOrganizations, searchPersons } from '../../lib/pickers';
import { labelClasses } from '../../pages/elections/InitiativeDetailPage';
import {
  ENDORSEMENT_SOURCE_TYPES,
  ENDORSER_KIND_LABELS,
  type EndorsementInput,
  type EndorsementRow,
} from '../../../constants/endorsements';

interface EndorsementModalProps {
  isOpen: boolean;
  /** Measures carry a side; candidate endorsements are always "for". */
  kind: 'candidate' | 'measure';
  /** null = adding */
  row: (EndorsementRow & { side?: string }) | null;
  /** The side a new measure endorsement starts on (the column whose Add was clicked). */
  defaultSide?: string;
  saving: boolean;
  /** The server's message, shown as-is. */
  error: string | null;
  onSave: (input: EndorsementInput) => void;
  onClose: () => void;
  /** Surface-specific note under the form (e.g. the candidate re-import warning). */
  note?: React.ReactNode;
}

const hint = 'mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary';
const blank = (v: string) => (v.trim() === '' ? null : v.trim());
const SIDE_OPTIONS = [
  { value: 'support', label: 'Support' },
  { value: 'oppose', label: 'Oppose' },
];

const personOption = (row: EndorsementRow | null): SearchOption | null =>
  row?.person_id ? { id: row.person_id, label: row.person_name || 'Linked person' } : null;

const organizationOption = (row: EndorsementRow | null): SearchOption | null =>
  row?.organization_id
    ? {
        id: `org:${row.organization_id}`,
        label: row.organization_name || 'Linked organization',
        sublabel: row.endorser_kind ? ENDORSER_KIND_LABELS[row.endorser_kind] : undefined,
      }
    : null;

/**
 * The one endorsement form, for candidate profiles and ballot measures
 * (ENDORSEMENTS_PRD §5.5): who endorsed (a person, a Capitol Commons org /
 * nonprofit / business from the registries, or a typed name), the side
 * (measures), the statement, the source and the date. The parent owns the
 * save; the server holds every rule and its message is shown as-is.
 */
const EndorsementModal: React.FC<EndorsementModalProps> = ({
  isOpen, kind, row, defaultSide = 'support', saving, error, onSave, onClose, note,
}) => {
  const [person, setPerson] = useState<SearchOption | null>(null);
  const [organization, setOrganization] = useState<SearchOption | null>(null);
  const [name, setName] = useState('');
  const [side, setSide] = useState(defaultSide);
  const [statement, setStatement] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setPerson(personOption(row));
    setOrganization(organizationOption(row));
    setName(row?.endorser_name ?? '');
    setSide(row?.side ?? defaultSide);
    setStatement(row?.statement ?? '');
    setSourceType(row?.source_type ?? '');
    setSourceName(row?.source_name ?? '');
    setSourceUrl(row?.source_url ?? '');
    setDate(row?.endorsed_on ? String(row.endorsed_on).slice(0, 10) : '');
  }, [isOpen, row, defaultSide]);

  const hasEndorser = Boolean(person || organization || name.trim());
  const hasSource = Boolean(sourceUrl.trim() || sourceName.trim());
  const sideLabel = side === 'oppose' ? 'oppose' : 'support';
  const title = `${row ? 'Edit' : 'Add'} ${kind === 'measure' ? `${sideLabel} ` : ''}endorsement`;

  const save = () =>
    onSave({
      ...(kind === 'measure' ? { side } : {}),
      person_id: person?.id ?? null,
      organization_ref: organization?.id ?? null,
      endorser_name: blank(name),
      statement: normalizeRichText(statement) || null,
      source_type: sourceType || null,
      source_name: blank(sourceName),
      source_url: blank(sourceUrl),
      endorsed_on: date || null,
    });

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title={title.charAt(0).toUpperCase() + title.slice(1)}
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" loading={saving} disabled={!hasEndorser || !hasSource || saving} onClick={save}>
            {row ? 'Save' : 'Add endorsement'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p role="alert" className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
        )}

        <fieldset className="space-y-3">
          <legend className={labelClasses}>Endorser *</legend>
          <div>
            <span className={labelClasses}>Person</span>
            <SearchSelect
              placeholder="Search people…"
              ariaLabel="Search for an endorsing person"
              fetchOptions={searchPersons}
              selected={person}
              onSelect={setPerson}
              onClear={() => setPerson(null)}
            />
          </div>
          <div>
            <span className={labelClasses}>Organization</span>
            <SearchSelect
              placeholder="Search orgs, nonprofits, or businesses…"
              ariaLabel="Search for an endorsing organization, nonprofit, or business"
              fetchOptions={searchEndorserOrganizations}
              selected={organization}
              onSelect={setOrganization}
              onClear={() => setOrganization(null)}
            />
          </div>
          <div>
            <label htmlFor="endorsement-name" className={labelClasses}>Or endorser name (free text)</label>
            <input
              id="endorsement-name"
              className="input"
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="For endorsers not in any registry"
            />
            <p className={hint}>Pick a person or organization, or type a name — at least one is required.</p>
          </div>
        </fieldset>

        {kind === 'measure' && (
          <div>
            <label htmlFor="endorsement-side" className={labelClasses}>Side</label>
            <select id="endorsement-side" className="input" value={side} onChange={(e) => setSide(e.target.value)}>
              {SIDE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}

        <div>
          <span className={labelClasses}>Statement</span>
          <QuillEditor value={statement} onChange={setStatement} placeholder="The endorsement statement, if one was made" />
        </div>

        <fieldset className="space-y-3">
          <legend className={labelClasses}>Source *</legend>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="endorsement-source-type" className={labelClasses}>Type</label>
              <select id="endorsement-source-type" className="input" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                <option value="">Not specified</option>
                {ENDORSEMENT_SOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="endorsement-source-name" className={labelClasses}>Name</label>
              <input
                id="endorsement-source-name"
                className="input"
                maxLength={255}
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                placeholder="e.g. Colorado AFL-CIO 2026 Endorsements"
              />
            </div>
            <div>
              <label htmlFor="endorsement-source-url" className={labelClasses}>URL</label>
              <input
                id="endorsement-source-url"
                type="url"
                className="input"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://"
              />
            </div>
            <div>
              <label htmlFor="endorsement-date" className={labelClasses}>Endorsed on</label>
              <input id="endorsement-date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <p className={hint}>Add the source&apos;s URL or name. Use the date on the source, or the day you found it if it has none.</p>
        </fieldset>

        {note}
      </div>
    </AdminModal>
  );
};

export default EndorsementModal;
